import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from 'store/rootStore';
import { AccountRepresentative, Assignee } from 'types/groupService';

export enum AssigneeStatuses {
  ASSIGNED = 'ASSIGNED',
  UNASSIGNED = 'UNASSIGNED',
}

interface AssigneeInStore extends Assignee {
  status: AssigneeStatuses;
}

export class AssignAgentsModalStore {
  public rootStore: RootStore;

  public selectedAccountRep = '';
  public assignees: AssigneeInStore[] = [];
  public agentsOfRecords: OptionType[] = [];
  public accountReps: OptionType[] = [];
  public loading = false;

  public get assignedAssignees(): AssigneeInStore[] {
    return this.assignees.filter((assignee) => assignee.status === AssigneeStatuses.ASSIGNED);
  }

  public get unassignedAssignees(): AssigneeInStore[] {
    return this.assignees.filter((assignee) => {
      return assignee.status === AssigneeStatuses.UNASSIGNED;
    });
  }

  public setSelectedAccountRepId(id: string): void {
    this.selectedAccountRep = id;
  }

  public setLoading(status: boolean): void {
    this.loading = status;
  }

  public setAssignees(assignees: AssigneeInStore[]): void {
    this.assignees = assignees;
  }

  public setAgentsOfRecords(agents: OptionType[]): void {
    this.agentsOfRecords = agents;
  }

  public setAccountReps(accountReps: OptionType[]): void {
    this.accountReps = accountReps;
  }

  changeAssigneeStatus(id: string, status: AssigneeStatuses): void {
    const assigneeToUpdate = this.assignees.find((assignee) => assignee.id === id);
    const filteredAssignees = this.assignees.filter((assignee) => assignee.id !== id);
    if (assigneeToUpdate) {
      this.setAssignees([...filteredAssignees, { ...assigneeToUpdate, status }]);
    } else {
      throw new Error('There is no assignee with such id!');
    }
  }

  async updateAssigneesAndAgentsOfRecord(): Promise<void> {
    this.setLoading(true);
    const currentAccountRep =
      this.rootStore.agentsStore.accountRepresentatives.find((celtic) => celtic.id === this.selectedAccountRep) || null;
    await this.rootStore.groupStore.saveAgentsAndAssignees(
      this.rootStore.groupStore.group.agentOfRecord?.id ?? '',
      this.assignedAssignees,
      currentAccountRep,
    );
    runInAction(() => {
      this.setLoading(false);
    });
  }

  updateLocalSelectedAccountRep(selectedAccountRepId: string): void {
    this.setSelectedAccountRepId(selectedAccountRepId);
  }

  updateLocalAssignees(assigneesInGroup: Assignee[], allAvailableAssignees: Assignee[]): void {
    const unassignedAssignees = allAvailableAssignees.filter(
      (generalAssignee) => !assigneesInGroup.some((assignedAssignee) => assignedAssignee.id === generalAssignee.id),
    );
    this.setAssignees([
      ...assigneesInGroup.map((assignee) => {
        return { ...assignee, status: AssigneeStatuses.ASSIGNED };
      }),
      ...unassignedAssignees.map((assignee) => {
        return { ...assignee, status: AssigneeStatuses.UNASSIGNED };
      }),
    ]);
  }

  updateLocalAccountReps(accountReps: AccountRepresentative[]): void {
    const groupAccountRep = this.rootStore.groupStore.group.accountRepresentative;
    const options = accountReps.map((celtic) => {
      return {
        value: celtic.id,
        label: `${celtic.firstName} ${celtic.lastName}`,
      };
    });

    if (groupAccountRep?.id === '') {
      options.push({
        value: 'null',
        // Adds a special option to represent a missing agent when the agentOfRecord ID is an empty string.
        // Using 'null' as the value because a select option with an empty string doesn't display label.
        label: `${groupAccountRep.firstName} ${groupAccountRep.lastName}`,
      });
    }

    this.setAccountReps(options);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
